import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Button, Heading, Loader, TextButton } from '@wix/design-system';
import { AiSparkleIcon } from './AiSparkleIcon';
import RegenerateSparkle from './RegenerateSparkle.svg';
import { useBusinessNames } from '../../hooks/useBusinessNames';
import { useIsMobile } from '../../hooks/useIsMobile';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import { BusinessNameTile } from './BusinessNameTile';
import { BusinessNameRow } from './BusinessNameRow';
import { BusinessNamesMobileCtaSkeleton, BusinessNameTileSkeleton } from './BusinessNameTileSkeleton';
import { BusinessNameTooltip } from './BusinessNameTooltip';
import { BusinessNamesError } from './BusinessNamesError';
import s from './BusinessNamesSection.scss';
export const BusinessNamesSection = _ref => {
  let {
    query,
    onSelectedNameChange,
    onUserSelectedName,
    onTileHoverChange,
    onTileTooltipView,
    onGenerateMore,
    onRegenerate,
    presentation = 'grid',
    onNamesShown
  } = _ref;
  const isMobile = useIsMobile();
  const isList = presentation === 'list';
  const {
    t
  } = DomainSearchGateModule.internals.useTranslations();
  const {
    names,
    selectedName,
    selectName,
    isLoading,
    isFetchingMore,
    hasError,
    ctaState,
    generateMore,
    regenerate,
    retry,
    namesPerBatch
  } = useBusinessNames({
    query
  });
  const lastReportedRef = useRef(undefined);
  useEffect(() => {
    const next = selectedName == null ? void 0 : selectedName.name;
    if (next && next !== lastReportedRef.current) {
      lastReportedRef.current = next;
      onSelectedNameChange == null || onSelectedNameChange(next);
    }
  }, [selectedName, onSelectedNameChange]);
  const notifiedNamesKeyRef = useRef(undefined);
  useEffect(() => {
    if (isLoading || names.length === 0) {
      return;
    }
    const key = `${query}::${JSON.stringify(names.map(n => n.id))}`;
    if (key === notifiedNamesKeyRef.current) {
      return;
    }
    notifiedNamesKeyRef.current = key;
    onNamesShown == null || onNamesShown(names);
  }, [isLoading, names, query, onNamesShown]);
  const handleSelect = useCallback(name => {
    selectName(name);
    if (name.name) {
      onUserSelectedName == null || onUserSelectedName(name.name);
    }
  }, [selectName, onUserSelectedName]);
  const handleCtaClick = useCallback(() => {
    if (ctaState === 'regenerate') {
      onRegenerate == null || onRegenerate();
      regenerate();
    } else {
      onGenerateMore == null || onGenerateMore();
      generateMore();
    }
  }, [ctaState, generateMore, regenerate, onGenerateMore, onRegenerate]);
  const openMobileInsights = useCallback(() => {
    if (!selectedName) {
      return;
    }
    if (selectedName.name) {
      onTileTooltipView == null || onTileTooltipView(selectedName.name);
    }
    DomainSearchGateModule.internals.insightsModalsController.businessNameInsights.show({
      name: selectedName
    });
  }, [onTileTooltipView, selectedName]);
  const loadingSkeletons = isLoading ? Array.from({
    length: namesPerBatch
  }, (_, i) => /*#__PURE__*/React.createElement(BusinessNameTileSkeleton, {
    key: `skeleton-${i}`
  })) : null;
  const fetchingMoreSkeletons = isFetchingMore ? Array.from({
    length: namesPerBatch
  }, (_, i) => /*#__PURE__*/React.createElement(BusinessNameTileSkeleton, {
    key: `skeleton-more-${i}`
  })) : null;
  const getNameKey = (name, idx) => name.id ?? String(idx);
  const selectedKey = selectedName == null ? void 0 : selectedName.id;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile ? isList ? 'SP4' : '24px' : 'SP5',
    width: "100%",
    dataHook: "business-names-section"
  }, hasError && names.length === 0 ? /*#__PURE__*/React.createElement(BusinessNamesError, {
    onRetry: retry
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    verticalAlign: isMobile ? 'top' : 'middle',
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0
  }, /*#__PURE__*/React.createElement(AiSparkleIcon, null)), /*#__PURE__*/React.createElement(Heading, {
    size: "small",
    as: "h2",
    className: s.sectionTitle
  }, t('domain_search_gate.bng_results.names_section.title'))), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile ? '20px' : 'SP5',
    width: "100%"
  }, isList ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "12px",
    width: "100%"
  }, isLoading ? loadingSkeletons : names.map((name, idx) => /*#__PURE__*/React.createElement(BusinessNameRow, {
    key: getNameKey(name, idx),
    name: name,
    onSelect: handleSelect
  })), fetchingMoreSkeletons) : /*#__PURE__*/React.createElement("div", {
    className: s.grid
  }, isLoading ? loadingSkeletons : names.map((name, idx) => {
    const isSelected = selectedKey != null && selectedKey === name.id;
    return /*#__PURE__*/React.createElement(BusinessNameTile, {
      key: getNameKey(name, idx),
      name: name,
      isSelected: isSelected,
      onSelect: handleSelect,
      onHoverChange: isMobile ? undefined : hovering => {
        if (hovering) {
          onTileHoverChange == null || onTileHoverChange(idx);
          if (name.name) {
            onTileTooltipView == null || onTileTooltipView(name.name);
          }
        }
      },
      tooltipContent: isMobile ? undefined : /*#__PURE__*/React.createElement(BusinessNameTooltip, {
        name: name
      })
    });
  }), fetchingMoreSkeletons), isMobile && (isLoading || isFetchingMore) && /*#__PURE__*/React.createElement(BusinessNamesMobileCtaSkeleton, null), hasError ? /*#__PURE__*/React.createElement(BusinessNamesError, {
    onRetry: handleCtaClick
  }) : names.length > 0 && /*#__PURE__*/React.createElement(Box, {
    align: "center",
    direction: isMobile ? 'vertical' : undefined,
    gap: isMobile ? 'SP7' : undefined,
    marginBottom: isMobile ? 'SP6' : undefined
  }, !isList && isMobile && selectedName && /*#__PURE__*/React.createElement(TextButton, {
    onClick: openMobileInsights,
    prefixIcon: /*#__PURE__*/React.createElement(Box, {
      marginRight: "SP0"
    }, /*#__PURE__*/React.createElement(AiSparkleIcon, null)),
    skin: "dark",
    size: "small",
    underline: "always",
    style: {
      color: 'var(--bng-funnel-purple)'
    },
    dataHook: "business-name-insights-cta"
  }, t('domains_search_gate.bng.mobile.results.name_suggestion.ai_insights.cta', {
    nameSuggestion: selectedName.name
  })), isMobile ? /*#__PURE__*/React.createElement(Button, {
    onClick: handleCtaClick,
    disabled: isFetchingMore,
    prefixIcon: isFetchingMore ? /*#__PURE__*/React.createElement(Box, {
      width: 18,
      height: 18,
      align: "center",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : /*#__PURE__*/React.createElement(RegenerateSparkle, {
      "aria-hidden": true
    }),
    skin: "dark",
    priority: "secondary",
    size: "medium",
    dataHook: "business-names-generate-button"
  }, t(ctaState === 'regenerate' ? 'domain_search_gate.bng_results.names_section.regenerate_new_names.cta' : 'domain_search_gate.bng_results.names_section.generate_more_names.cta')) : /*#__PURE__*/React.createElement(TextButton, {
    onClick: handleCtaClick,
    disabled: isFetchingMore,
    prefixIcon: isFetchingMore ? /*#__PURE__*/React.createElement(Box, {
      width: 18,
      height: 18,
      align: "center",
      verticalAlign: "middle",
      paddingInlineEnd: "6px"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : /*#__PURE__*/React.createElement(RegenerateSparkle, {
      "aria-hidden": true
    }),
    skin: "dark",
    size: "small",
    underline: "always",
    dataHook: "business-names-cta"
  }, t(ctaState === 'regenerate' ? 'domain_search_gate.bng_results.names_section.regenerate_new_names.cta' : 'domain_search_gate.bng_results.names_section.generate_more_names.cta'))))));
};
//# sourceMappingURL=BusinessNamesSection.js.map